// The Work transport. The run lifecycle, the submit pipeline, the approval mode,
// generated titles, discard and restore, and the turn payloads are decided here.
// The bound hook at the bottom reads the host ports when a turn needs them.
import { invalidateCreditAllowance } from '@heidi/commerce/data';
import { toast } from '@heidi/design-system/toast';
import { rpaBridge } from '@heidi/plugin-rpa';
import { isTauri } from '@heidi/runtime/platform';
import { useQueryDocumentById, useQuerySessionById } from '@heidi/session/data';
import { useLatestRef } from '@heidi/utils';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import canonicalize from 'canonicalize';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  DEFAULT_WORK_CHAT_APPROVAL_MODE,
  type AgentRunStatus,
  type ApprovalMode,
  type ApprovalResolution,
  type ApprovalVerdict,
  type AttachmentBlock,
  type ChatItem,
  type ChildRunSummary,
  type LLMStreamConnectionStatus,
  type Message,
  type RunTitleGeneratedAgentEvent,
  type WorkRunTriggerType,
} from '../../../contracts/harness.types';
import { projectsQueryKeys } from '../../../contracts/keys';
import { deriveProjectShareGating } from '../../../contracts/project-share-gating';
import type { ProjectDataScope, ProjectDetail } from '../../../contracts/projects.types';
import type {
  ProjectTurnDesktopIdentityProof,
  ProjectTurnDesktopPort,
  ProjectTurnDesktopTarget,
} from '../../../contracts/projects/project-turn-desktop.types';
import { projectAttachmentsToBlocks } from '../../../contracts/projects/projectOptimisticAttachments';
import type { ViewedDocumentContext } from '../../../contracts/projects/requests.types';
import type { WorkspaceSnapshotV1 } from '../../../contracts/projects/workspace.types';
import { mintTurnId, type TurnOperation } from '../../../contracts/turn-identity';
import type {
  TrackProjectChatErrorOnce,
  TrackWorkApprovalActioned,
  TrackWorkBilledEvent,
} from '../../../contracts/work-launch-analytics.types';
import type { ChildRunDiscovery } from '../../../data/harness/child-runs/discovery';
import { createRefusedTurnOperation } from '../../../data/harness/turn-operation-registry';
import { useMutateSetApprovalMode } from '../../../data/mutation/useMutateSetApprovalMode';
import {
  buildWorkReferencedObjectProps,
  resolveWorkSkillCount,
} from '../../../data/projects/analytics/workReferencedObjects';
import { WorkTurnMetricsTracker } from '../../../data/projects/analytics/workTurnMetrics';
import { getProjectClientCapabilities } from '../../../data/projects/clientCapabilities';
import { useProject, type useSubmitProjectTurn } from '../../../data/projects/hooks';
import { useProjectRunStreamObserver } from '../../../data/projects/hooks/useProjectRunStreamObserver';
import {
  extractSlideStreamUpdates,
  type SlideStreamUpdate,
} from '../../../data/projects/presentation/slide-stream-updates';
import {
  hasProjectsComposerSkill,
  type ProjectsComposerPromptDraft,
} from '../../../data/projects/projects-composer-editor-draft';
import type { SubmitProjectTurnArgs } from '../../../data/projects/submit-project-turn-args';
import type { DiscardableTurn } from '../../../data/projects/useRestoreDiscardedDraft';
import {
  useViewedDocumentContext,
  type ViewedDocumentReads,
} from '../../../data/projects/workspace/useViewedDocumentContext';
import type { RunStreamEventObserver } from '../../../data/query/useRunStreamLifecycle';
import { useWorkChatApprovalModePreference } from '../../../data/query/useWorkChatApprovalMode';
import { useAgentRunChat } from '../../agent-harness/use-agent-run-chat';
import { useDesktopTargetViewSelection } from '../../work-home/use-desktop-target-view-selection';
import { getWorkHostPort } from '../../work-home/work-home-ports';
import {
  invalidateLists,
  useSubmitProjectTurn as useBoundSubmitProjectTurn,
} from '../hooks';
import { uploadStagedFiles } from '../uploadStagedFiles';
import { useWorkspaceSnapshot } from '../workspace/useWorkspaceSnapshot';

/**
 * What the app does when a turn reaches a terminal stream boundary (cancelled,
 * completed or failed), in the order the transport calls them.
 */
export interface ProjectTurnSettledReactions {
  /** First: the credit allowance the turn spent. */
  invalidateCreditAllowance: () => void;
  /** Last, after the run-completed emit: the app-shell caches an agent event moves. */
  invalidateShellCaches: (queryClient: QueryClient) => void;
}

/**
 * The app half of the Work transport. Pass ONE module-level object: the stream
 * observer and the submit callback list its members as dependencies, so a fresh
 * object per render would rebuild them.
 */
export interface ProjectTransportPorts {
  /** The turn POST, bound to the app's caches a turn moves (a hook). */
  useSubmitTurn: (
    projectId: string
  ) => Pick<ReturnType<typeof useSubmitProjectTurn>, 'mutateAsync'>;
  /** The project lists and the app's other lists that show projects. */
  invalidateLists: (queryClient: QueryClient) => void;
  /** The native desktop bridge (a hook; the binding memoises what it returns). */
  useDesktopPort: () => ProjectTurnDesktopPort;
  /** The reads behind the viewed-document context. */
  viewedDocumentReads: ViewedDocumentReads;
  /** The stream consumers outside this domain, which this chat is the one owner of. */
  onForeignStreamEvent: RunStreamEventObserver;
  turnSettled: ProjectTurnSettledReactions;
  trackWorkRunCompleted: TrackWorkBilledEvent;
  trackWorkMessageSubmitted: TrackWorkBilledEvent;
  trackWorkApprovalActioned: TrackWorkApprovalActioned;
  /** The app's fired-once emit, so its guard stays one instance. */
  trackProjectChatErrorOnce: TrackProjectChatErrorOnce;
}

/** What the host resolves for this chat on every render. */
export interface ProjectTransportOptions {
  /**
   * The screen snapshot a turn carries: the canvas snapshot (Work-home
   * `openArtifactAtom`), or a host's own when no canvas is mounted
   * (Work-in-session, where the LHS tabs are the screen).
   */
  workspace: WorkspaceSnapshotV1;
  /** Limit desktop-target resolution to explicit computer-use commands. */
  desktopTargetRequirement?: 'all-turns' | 'computer-use-only';
  /** The view's manual computer choice; `null` when none was made. */
  desktopTargetOverride: string | null;
}

const submitTurnErrorMessages = defineMessages({
  computerUseDesktopOnly: {
    defaultMessage: 'Working the screen is only available in the desktop app.',
    description: 'Error shown when a computer-use skill is sent from the web app',
    id: 'JO61vdq9it',
  },
  computerUsePermissionDenied: {
    defaultMessage: 'Grant desktop permissions, restart Heidi, then send again.',
    description:
      'Error shown when desktop permissions are missing for a computer-use turn',
    id: 'Itmxwho4dX',
  },
  projectRunNotReady: {
    defaultMessage: 'This chat is still opening. Try sending again in a moment.',
    description: 'Error shown when a message is sent before the project run is ready',
    id: 'Scqvaafejn',
  },
  viewOnly: {
    defaultMessage: 'This thread is view only.',
    id: 'vAVgAtiPXi',
    description: 'Error when a view-only grantee tries to send a turn',
  },
});

/** A second start while one is in flight. The queue upstream is what
 *  serializes sends — an overlapping POST was already a broken path (the BE
 *  refuses concurrent turns), so the transport refuses it before any effect. */
class TurnOverlapError extends Error {
  constructor() {
    super('A turn submission is already in flight');
    this.name = 'TurnOverlapError';
  }
}

export interface ProjectTransport {
  /** Reconciled harness timeline (snapshot + live stream). */
  items: ChatItem[];
  agentRunId: string | undefined;
  /**
   * Computer-use sub-agents discovered on this run's per-run stream + snapshot,
   * awaiting executor attach. Per-run-local (no global store); the executor
   * attach consumes it to hand each child to the Rust executor.
   */
  computerUseDiscoveries: ChildRunDiscovery[];
  /** Child-run summaries from the parent run snapshot. */
  childRuns: ChildRunSummary[];
  /** Backend-frozen desktop target mode; absent legacy responses are false. */
  desktopTargetEnabled?: boolean;
  /** Access scope frozen at create. Absent on legacy responses. */
  dataScope?: ProjectDataScope;
  /**
   * Live content of the document currently visible beside the chat, with the
   * session it belongs to. The turn request sends it without `session_id`.
   */
  viewedDocument?: ViewedDocumentContext & { session_id?: string };
  /** Harness run lifecycle status (`running` | `closed` | `failed`). */
  status: AgentRunStatus | undefined;
  /** Backend-confirmed recording-child binding on this Project's current run. */
  recordingEligible: boolean;
  /** Viewer of a live-grant: transcript visible, composer locked. */
  readOnly: boolean;
  ownerDisplayName: string | null;
  canFork: boolean;
  streamStatus: LLMStreamConnectionStatus;
  /** Auto-generated run title; surfaced live off the snapshot. */
  title: string | null | undefined;
  /** True until the first run snapshot (or the project detail) resolves. */
  isLoading: boolean;
  /** A turn is active (submitted, running, or paused on approval). */
  turnInFlight: boolean;
  /** The submit pipeline (attachment upload + turn POST) is in flight. */
  isSubmitting: boolean;
  /** True while a cancel-turn request is in flight. */
  cancelling: boolean;
  /**
   * Start exactly one turn. Registers terminal completion before upload or
   * POST. Never throws: overlap / not-ready return a refused operation.
   */
  startTurn: (command: SubmitProjectTurnArgs) => TurnOperation;
  approvalMode: ApprovalMode;
  /** Optimistic; PATCHes the run when one exists, reverts + toasts on failure. */
  setApprovalMode: (next: ApprovalMode) => void;
  /** Tool-call ids the run is currently paused on, from `run.approvals`. */
  pendingApprovalToolCallIds: Set<string>;
  /**
   * Resolve a harness HITL approval inline from a project transcript
   * block. Returns a Promise that rejects on BE failure so the inline
   * card can clear its pending-verdict latch and let the user retry.
   * Resolves on success; the card stays mounted until the harness
   * SSE refetch transitions the gating ``tool_use`` out of
   * ``pending_approval``.
   */
  resolveApproval: (
    approvalId: string,
    verdict: ApprovalVerdict,
    reason?: string,
    modifiedInput?: Record<string, unknown> | null,
    resolution?: ApprovalResolution
  ) => Promise<void>;
  /** True while the harness approval resolution mutation is in flight. */
  resolvingApproval: boolean;
  /** Contextual thinking-loader phrases for the active turn (agent-events SSE). */
  loaderPhrases: string[] | undefined;
  /** Cancel the parent's active turn (BE cascade-cancels any sub-agent child). */
  cancelTurn: () => void;
  /**
   * Bumps each time a turn is cancelled before the backend persisted anything
   * for it (a pre-output stop the backend discards). The composer watches this
   * to return the just-cancelled prompt to the input box. Starts at 0.
   */
  discardedTurnNonce: number;
  /**
   * The just-discarded turn's restorable composer state, remembered at submit
   * time (any submit path: composer, seeded first turn, published submit). The
   * composer replays it on a ``discardedTurnNonce`` bump. Null until a discard.
   */
  discardedTurn: DiscardableTurn | null;
}

const textDraft = (text: string): ProjectsComposerPromptDraft => ({
  text,
  segments: [],
});

// A turn captured at submit time that must never be restored: a computer-use
// skill turn whose draft can't round-trip the skill (a flattened seeded draft,
// or no rich draft at all) — restoring it would silently downgrade the skill to
// plain text. Deliberately NOT `null`: the discard fallback (`discardCandidateRef
// ?? lastRestorableTurnRef`) would fall through a null candidate to whatever a
// draining queued send wrote into `lastRestorableTurnRef`, restoring an
// in-flight queued turn into the composer. The sentinel survives that `??`.
const NON_RESTORABLE = Symbol('non-restorable-turn');
type RestorableCapture = DiscardableTurn | typeof NON_RESTORABLE | null;

/**
 * Remember a restorable form of this turn before it leaves, so a pre-output
 * cancel can return it to the composer no matter which path submitted it.
 * A turn is restorable only if it can be replayed faithfully:
 *   - Composer turns carry the rich editor draft with its segments intact,
 *     so `buildTurn` recompiles any computer-use skill from those segments
 *     on resend — restorable, skill and all.
 *   - The seeded path passes a *flattened* restoreContext (`segments: []`):
 *     fine for plain / linked text, but a computer-use skill can't survive
 *     it (no segments to recompile). Such a turn — and any skill turn with
 *     no rich draft — stays NON_RESTORABLE; restoring it would silently
 *     downgrade the skill to plain text.
 * `hasProjectsComposerSkill` is the same predicate `buildTurn` uses to
 * decide whether to recompile the skill, so it exactly captures "does this
 * draft round-trip the skill". NON_RESTORABLE is a sentinel, not `null`, so
 * the discard fallback (`discardCandidateRef ?? lastRestorableTurnRef`)
 * can't fall through to a queued turn that drained after the cancel.
 *   - A `note_push_plan` (panel-driven apply) turn is ALSO NON_RESTORABLE:
 *     the composer replays a text/segment DRAFT, which can't carry the
 *     structured plan, so restoring it as plain text would let a resend POST
 *     "Apply the confirmed…" with NO plan (the mapping silently unapplied).
 *     Retaining+replaying the captured plan is worse — a later manual resend
 *     would submit a plan built against a STALE note_revision (exactly what
 *     the 409 machinery guards). The plan's re-entry surface is the panel's
 *     Confirm & apply, which rebuilds a fresh plan from surviving candidate
 *     state against the current revision — so a cancel simply forces that.
 */
const resolveRestorableCapture = (args: SubmitProjectTurnArgs): RestorableCapture => {
  const skillRoundTrips =
    !args.computerUseSkill ||
    (args.restoreContext ? hasProjectsComposerSkill(args.restoreContext.draft) : false);
  return args.restoreContext && skillRoundTrips && !args.notePushPlan
    ? {
        draft: args.restoreContext.draft,
        files: args.files,
        linkedItems: args.restoreContext.linkedItems,
      }
    : args.computerUseSkill || args.notePushPlan
      ? NON_RESTORABLE
      : { draft: textDraft(args.displayText ?? args.text), files: args.files };
};

const optimisticUserMessage = (
  text: string,
  attachments: AttachmentBlock[] = []
): Message => ({
  role: 'user',
  blocks: [{ kind: 'text', text }, ...attachments],
});

/**
 * The single Projects ↔ agent-harness glue hook (a hybrid transport).
 *
 * READ path is pure harness: a project's durable `agent_run_id` runs
 * through `useAgentRunChat`, so the transcript, streaming, and
 * lifecycle (status / title / approvals / failure) are entirely
 * agent-harness — no `/projects/{id}/messages` polling.
 *
 * WRITE path is the Projects client, because the harness turn has no
 * attachment support (`capabilities.attachments === false`): we hand
 * the harness an optimistic user message first — the bubble is up on
 * the frame the user hits send — then pre-upload any staged files via
 * `uploadStagedFiles` to mint `attachment_ids` and POST a prompt-shaped
 * turn through `useSubmitProjectTurn` (`POST /projects/{id}/turns`).
 * The submission handle settles that bubble when the round-trip resolves.
 */
export const useProjectTransportCore = (
  projectId: string | undefined,
  options: ProjectTransportOptions,
  ports: ProjectTransportPorts
): ProjectTransport => {
  const {
    useSubmitTurn,
    invalidateLists,
    useDesktopPort,
    viewedDocumentReads,
    onForeignStreamEvent,
    turnSettled,
    trackWorkRunCompleted,
    trackWorkMessageSubmitted,
    trackWorkApprovalActioned,
    trackProjectChatErrorOnce,
  } = ports;
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { desktopTargetOverride } = options;
  const projectQuery = useProject(projectId);
  const agentRunId = projectQuery.data?.agent_run_id;
  const recordingEligible = projectQuery.data?.recording_eligible === true;
  const desktopTargetEnabled = projectQuery.data?.desktop_target_enabled === true;
  const shareGating = deriveProjectShareGating(projectQuery.data);
  // Destructure the stable fields: depending on the whole query object would
  // rebuild the submit callback every render (react-query returns a new
  // object reference each time). The desktop port is memoised by the binding,
  // on its permission gate and query client.
  const desktop = useDesktopPort();

  // Per-run-local computer-use discovery — fed by this run's per-run stream
  // (live) and snapshot (durable safety net), kept on THIS run's own state (no
  // global store; the only global piece is the Rust executor). The executor
  // attach consumes the list per-run to hand each child to that executor.
  const {
    discoveries: computerUseDiscoveries,
    onStreamEvent,
    reconcileFromSnapshot,
  } = useProjectRunStreamObserver(agentRunId, { onForeignStreamEvent });

  // A restorable form of the most recently submitted turn, captured at submit
  // time so a pre-output discard can return it to the composer — regardless of
  // which path submitted it (composer, seeded first turn, published submit).
  const lastRestorableTurnRef = useRef<RestorableCapture>(null);
  // The cancelled turn's restorable form, snapshotted at the `turn_cancelled`
  // event (below) — BEFORE a queued send can drain and overwrite
  // `lastRestorableTurnRef` — so a discard restores the cancelled turn, not the
  // one the queue just dispatched.
  const discardCandidateRef = useRef<RestorableCapture>(null);
  // Bumped (with the discarded turn) when the harness reports a pre-output
  // discard (a turn cancelled before the backend persisted anything). The
  // composer consumes these to restore the cancelled prompt to the input box.
  const [discardedTurnNonce, setDiscardedTurnNonce] = useState(0);
  const [discardedTurn, setDiscardedTurn] = useState<DiscardableTurn | null>(null);
  const onTurnDiscarded = useCallback(() => {
    const captured = discardCandidateRef.current ?? lastRestorableTurnRef.current;
    setDiscardedTurn(captured === NON_RESTORABLE ? null : captured);
    setDiscardedTurnNonce((nonce) => nonce + 1);
    discardCandidateRef.current = null;
  }, []);

  // Per-turn stream metrics for work_run_completed (TTFT, duration, lengths).
  const turnMetricsRef = useRef(new WorkTurnMetricsTracker());
  // The local turn awaiting its run_completed. A backend without the identity
  // contract mints its own turn id and omits it from stream frames, so the
  // accepted id is tracked too and an unnamed terminal settles an accepted turn.
  const completionTurnRef = useRef<{
    turnId: string;
    acceptedTurnId: string | null;
  } | null>(null);
  // Props captured at submit so the matching run_completed can reuse them.
  const lastSubmitAnalyticsRef = useRef<{
    query_length?: number;
    approval_mode?: ApprovalMode;
    trigger_type?: WorkRunTriggerType;
    surface?: 'work_home' | 'in_session';
    turn_index?: number;
    skill_count?: number;
    referenced_object_types?: Array<
      'session' | 'patient' | 'file' | 'source' | 'routine'
    >;
    referenced_object_count?: number;
  }>({});

  // Capture the cancelled turn synchronously at the `turn_cancelled` boundary
  // (the stream observer fires before the harness drains the queue), then fan
  // the event on to the computer-use discovery observer.
  const observeStream = useCallback<RunStreamEventObserver>(
    (id, event) => {
      turnMetricsRef.current.observe(event);
      if (
        event.type === 'turn_cancelled' ||
        event.type === 'turn_completed' ||
        event.type === 'turn_failed'
      ) {
        turnSettled.invalidateCreditAllowance();
        const metrics = turnMetricsRef.current.snapshot();
        const submitProps = lastSubmitAnalyticsRef.current;
        const trackedTurn = completionTurnRef.current;
        const terminalTurnId = event.turn_id;
        if (event.type === 'turn_cancelled') {
          discardCandidateRef.current = lastRestorableTurnRef.current;
        }
        // This frontend completion exists only for turns submitted through this
        // transport. Unattended routines are covered by backend Turn and Job
        // completion events instead, so no Run event is expected for them.
        if (
          trackedTurn != null &&
          (terminalTurnId == null
            ? trackedTurn.acceptedTurnId != null
            : terminalTurnId === trackedTurn.turnId ||
              terminalTurnId === trackedTurn.acceptedTurnId)
        ) {
          completionTurnRef.current = null;
          if (event.type === 'turn_cancelled') {
            trackWorkRunCompleted((billingProps) => ({
              action: 'aborted',
              outcome: 'cancelled',
              object_type: 'turn',
              project_id: projectId,
              agent_run_id: agentRunId,
              surface: 'work_home',
              ...billingProps,
              ...metrics,
              ...submitProps,
            }));
          } else if (event.type === 'turn_completed') {
            trackWorkRunCompleted((billingProps) => ({
              action: 'completed',
              outcome: 'succeeded',
              object_type: 'turn',
              project_id: projectId,
              agent_run_id: agentRunId,
              surface: 'work_home',
              ...billingProps,
              ...metrics,
              ...submitProps,
            }));
          } else {
            trackWorkRunCompleted((billingProps) => ({
              action: 'completed',
              outcome: 'failed',
              object_type: 'turn',
              project_id: projectId,
              agent_run_id: agentRunId,
              surface: 'work_home',
              error_code: 'run_failed',
              ...billingProps,
              ...metrics,
              ...submitProps,
            }));
          }
          lastSubmitAnalyticsRef.current = {};
          turnMetricsRef.current.reset();
        }
        turnSettled.invalidateShellCaches(queryClient);
      }
      onStreamEvent(id, event);
    },
    [
      agentRunId,
      onStreamEvent,
      projectId,
      queryClient,
      trackWorkRunCompleted,
      turnSettled,
    ]
  );

  // The header and lists read `project.title`, seeded as "New chat" at create.
  // `run.title_generated` is the live update; the snapshot mirror below is the
  // backstop when that publish is missed. A manual rename wins.
  const applyGeneratedTitle = useCallback(
    (title: string | null | undefined) => {
      if (!projectId || !title) return;
      const detail = projectQuery.data;
      if (!detail || detail.title === title || detail.title_is_custom) return;
      queryClient.setQueryData<ProjectDetail>(projectsQueryKeys.detail(projectId), {
        ...detail,
        title,
      });
      invalidateLists(queryClient);
    },
    [invalidateLists, projectId, projectQuery.data, queryClient]
  );
  const runTitleGenerated = useCallback(
    (event: RunTitleGeneratedAgentEvent) => {
      applyGeneratedTitle(event.payload.title);
    },
    [applyGeneratedTitle]
  );

  const chat = useAgentRunChat(agentRunId, {
    onStreamEvent: observeStream,
    // Title events (`run.title_generated`) must not be gated on the loader-phrases flag.
    subscribeToEvents: true,
    eventHandlers: { runTitleGenerated },
    onTurnDiscarded,
    // Pre-output cancels leave no trace in Projects: drop the bubble + marker
    // from the timeline (here) and return the prompt to the composer (via the
    // `discardedTurnNonce` bump in `onTurnDiscarded` above).
    dropEmptyCancelledTurns: true,
    // Cross-device chat sync: hold the per-run stream open across turns so a
    // turn started on another device (mobile) streams into this one (web/desktop)
    // without a manual refresh.
    follow: true,
  });
  const { beginTurnOperation } = chat;
  const chatItemsRef = useLatestRef(chat.items);
  const runTriggerTypeRef = useLatestRef(chat.run?.trigger_type ?? 'chat');

  useEffect(() => {
    reconcileFromSnapshot(chat.run);
  }, [chat.run, reconcileFromSnapshot]);
  const { mutateAsync: submitTurnAsync } = useSubmitTurn(projectId ?? '');

  // Override clears once the snapshot catches up, so remote flips show through.
  const [approvalModeOverride, setApprovalModeOverride] = useState<ApprovalMode | null>(
    null
  );
  const snapshotApprovalMode = chat.run?.approval_mode ?? null;
  useEffect(() => {
    if (snapshotApprovalMode && approvalModeOverride === snapshotApprovalMode) {
      setApprovalModeOverride(null);
    }
  }, [snapshotApprovalMode, approvalModeOverride]);
  const { persistedApprovalMode, rememberApprovalMode } =
    useWorkChatApprovalModePreference();
  // Display-only seed: this hook never starts a run, so a new chat's remembered
  // pick reaches the create through `ProjectChat`, not from here.
  const seedApprovalMode = agentRunId ? null : persistedApprovalMode;
  // Null = no pick and no read-back; the turn POST then omits the field.
  const knownApprovalMode =
    approvalModeOverride ?? snapshotApprovalMode ?? seedApprovalMode;
  const approvalMode: ApprovalMode = knownApprovalMode ?? DEFAULT_WORK_CHAT_APPROVAL_MODE;
  const approvalModeRef = useLatestRef(approvalMode);
  const knownApprovalModeRef = useLatestRef(knownApprovalMode);

  const { mutate: patchApprovalMode } = useMutateSetApprovalMode();
  const setApprovalMode = useCallback(
    (next: ApprovalMode) => {
      const previous = approvalModeRef.current;
      if (next === previous) return;
      setApprovalModeOverride(next);
      // No run yet (provisional / pre-create) — the pick rides the next POST.
      if (!agentRunId) {
        rememberApprovalMode(next);
        return;
      }
      patchApprovalMode(
        { agentRunId, approvalMode: next },
        {
          // Only a mode that actually took effect is worth remembering.
          onSuccess: () => rememberApprovalMode(next),
          onError: () => {
            setApprovalModeOverride(previous);
            toast({ title: "Couldn't change approval mode", tone: 'destructive' });
          },
        }
      );
    },
    [agentRunId, approvalModeRef, patchApprovalMode, rememberApprovalMode]
  );

  // Covers the whole submit pipeline — sequential attachment upload AND
  // the turn POST — so the composer disables for the entire round-trip.
  // The mutation's own `isPending` would only cover the POST.
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Snapshot of the canvas (open artifact + recording lifecycle), resolved by the
  // host. Read through a latest-ref so the long-lived ``submit`` callback
  // captures the value at send time without churning its dependency array on
  // every artifact/tab/recording change.
  const { workspace } = options;
  const workspaceRef = useLatestRef(workspace);
  const viewedDocument = useViewedDocumentContext(workspace, viewedDocumentReads);
  const viewedDocumentRef = useLatestRef(viewedDocument);
  const viewedSessionId = workspace?.open_artifact?.session_id;
  const viewedDocumentInSession = useMemo(
    () =>
      viewedDocument && viewedSessionId
        ? { ...viewedDocument, session_id: viewedSessionId }
        : viewedDocument,
    [viewedDocument, viewedSessionId]
  );
  const desktopTargetRequirement = options.desktopTargetRequirement ?? 'all-turns';

  const liveTitle = chat.run?.title;
  useEffect(() => {
    applyGeneratedTitle(liveTitle);
  }, [applyGeneratedTitle, liveTitle]);

  const submitInFlightRef = useRef(false);

  const startTurn = useCallback(
    (args: SubmitProjectTurnArgs): TurnOperation => {
      const turnId = args.turnId ?? mintTurnId();
      if (submitInFlightRef.current) {
        return createRefusedTurnOperation(turnId, new TurnOverlapError());
      }
      if (!projectId || !agentRunId) {
        // The run id hasn't resolved yet. The composer has already cleared and
        // disabled itself, so resolving silently would lose the message and
        // leave the box dead — it needs the rejection to undo both.
        return createRefusedTurnOperation(
          turnId,
          new Error(intl.formatMessage(submitTurnErrorMessages.projectRunNotReady))
        );
      }
      if (shareGating.readOnly) {
        return createRefusedTurnOperation(
          turnId,
          new Error(intl.formatMessage(submitTurnErrorMessages.viewOnly))
        );
      }
      const wirePrompt = args.text;
      lastRestorableTurnRef.current = resolveRestorableCapture(args);
      // Adopt a seeded turn's pick so the toggle matches what this send seeds.
      const turnApprovalMode = args.approvalMode ?? knownApprovalModeRef.current;
      if (args.approvalMode && args.approvalMode !== approvalModeRef.current) {
        setApprovalModeOverride(args.approvalMode);
      }
      // Register terminal completion before any upload, permission await, or POST.
      const handle = beginTurnOperation({ turnId });
      turnMetricsRef.current.markStarted();
      completionTurnRef.current = { turnId: handle.turnId, acceptedTurnId: null };
      lastSubmitAnalyticsRef.current = {
        query_length: wirePrompt.length,
        approval_mode: turnApprovalMode ?? approvalModeRef.current,
        trigger_type: runTriggerTypeRef.current,
        surface: args.analytics?.surface === 'in_session' ? 'in_session' : 'work_home',
      };
      // The wire prompt, not the display text: the persisted twin carries the
      // wire text and the no-echo reconcile pairs by content, so a display-text
      // bubble would never pair. The renderer strips the `[CONTEXT]` block.
      // Attachments still to upload are the one thing worth waiting for: the
      // bubble holds until their ids exist so it paints with its chips.
      const needsDeferredBubble =
        (!args.attachment_ids?.length && Boolean(args.files?.length)) ||
        Boolean(args.computerUseSkill);
      if (!needsDeferredBubble) {
        try {
          handle.ingestOptimistic([
            optimisticUserMessage(wirePrompt, args.optimisticAttachments ?? []),
          ]);
        } catch (error) {
          handle.reject(error);
          return handle.operation;
        }
      }
      submitInFlightRef.current = true;
      setIsSubmitting(true);

      const runPipeline = async (): Promise<void> => {
        try {
          const isDesktopBridgeAvailable = desktop.isAvailable();
          if (
            !desktopTargetEnabled &&
            args.computerUseSkill &&
            !isDesktopBridgeAvailable
          ) {
            trackWorkApprovalActioned({
              project_id: projectId,
              agent_run_id: agentRunId,
              surface: 'project_chat',
              action: 'shown',
              outcome: 'denied',
              object_type: 'rpa',
              error_code: 'desktop_required',
            });
            throw new Error(
              intl.formatMessage(submitTurnErrorMessages.computerUseDesktopOnly)
            );
          }
          if (!desktopTargetEnabled && args.computerUseSkill) {
            trackWorkApprovalActioned({
              project_id: projectId,
              agent_run_id: agentRunId,
              surface: 'project_chat',
              action: 'prompted',
              object_type: 'rpa',
            });
            const permissionsOk = await desktop.ensureComputerUsePermissions();
            if (!permissionsOk) {
              trackWorkApprovalActioned({
                project_id: projectId,
                agent_run_id: agentRunId,
                surface: 'project_chat',
                action: 'resolved',
                outcome: 'denied',
                object_type: 'rpa',
              });
              throw new Error(
                intl.formatMessage(submitTurnErrorMessages.computerUsePermissionDenied)
              );
            }
            trackWorkApprovalActioned({
              project_id: projectId,
              agent_run_id: agentRunId,
              surface: 'project_chat',
              action: 'resolved',
              outcome: 'granted',
              object_type: 'rpa',
            });
          }
          const shouldResolveDesktopTarget =
            desktopTargetEnabled &&
            (desktopTargetRequirement === 'all-turns' || Boolean(args.computerUseSkill));
          const resolvedTarget = await desktop.resolveDesktopTarget({
            enabled: shouldResolveDesktopTarget,
            override: args.desktopTargetOverride ?? desktopTargetOverride,
          });

          // Legacy mode probes triggering-client capabilities. Target mode
          // sends the selected registered target instead; these execution
          // contracts are intentionally mutually exclusive after creation.
          const needsUpload = !args.attachment_ids?.length && Boolean(args.files?.length);
          let uploadedAttachments:
            | Awaited<ReturnType<typeof uploadStagedFiles>>
            | undefined;
          let optimisticAttachments = args.optimisticAttachments ?? [];

          if (needsUpload) {
            uploadedAttachments = await uploadStagedFiles({
              projectId,
              files: args.files!,
            });
            optimisticAttachments = projectAttachmentsToBlocks(uploadedAttachments);
            handle.ingestOptimistic([
              optimisticUserMessage(wirePrompt, optimisticAttachments),
            ]);
          } else if (needsDeferredBubble) {
            handle.ingestOptimistic([
              optimisticUserMessage(wirePrompt, optimisticAttachments),
            ]);
          }

          const client_capabilities =
            !desktopTargetEnabled && isDesktopBridgeAvailable
              ? await desktop.getClientCapabilities()
              : null;
          const attachment_ids = args.attachment_ids?.length
            ? args.attachment_ids
            : uploadedAttachments?.length
              ? uploadedAttachments.map((attachment) => attachment.id)
              : undefined;
          const initiatingDesktopEnabled =
            desktopTargetEnabled && isDesktopBridgeAvailable;
          const initiatingDesktopInstanceId = initiatingDesktopEnabled
            ? await desktop.getInstanceId().catch(() => null)
            : null;
          const origin_surface: 'in_session' | 'work_home' =
            args.analytics?.surface === 'in_session' ? 'in_session' : 'work_home';
          const unsignedTurnBody = {
            prompt: wirePrompt,
            ...(args.routineName?.trim() && { routine_name: args.routineName.trim() }),
            attachment_ids,
            ...(turnApprovalMode && { approval_mode: turnApprovalMode }),
            origin_surface,
            ...(client_capabilities && { client_capabilities }),
            ...(resolvedTarget.status === 'send'
              ? { target_id: resolvedTarget.targetId }
              : {}),
            ...(initiatingDesktopEnabled && initiatingDesktopInstanceId
              ? { initiating_desktop_instance_id: initiatingDesktopInstanceId }
              : {}),
            ...((desktopTargetEnabled || isDesktopBridgeAvailable) &&
              args.computerUseSkill && { computer_use_skill: args.computerUseSkill }),
            ...(args.notePushPlan && { note_push_plan: args.notePushPlan }),
            ...(workspaceRef.current && { workspace: workspaceRef.current }),
            ...(viewedDocumentRef.current && {
              viewed_document: viewedDocumentRef.current,
            }),
            turn_id: handle.turnId,
          };
          // The backend canonicalizes the same unsigned JSON with RFC 8785;
          // the native key signs its SHA-256 digest, along with routing identity.
          let desktopIdentityProof: ProjectTurnDesktopIdentityProof | undefined;
          if (isTauri() && resolvedTarget.status === 'send') {
            desktopIdentityProof = await desktop.signProjectTurn({
              projectId,
              turnId: handle.turnId,
              targetId: resolvedTarget.targetId,
              unsignedBody: unsignedTurnBody,
            });
          }
          const turnBody = {
            ...unsignedTurnBody,
            ...(desktopIdentityProof && {
              desktop_identity_proof: {
                instance_id: desktopIdentityProof.instanceId,
                signature: desktopIdentityProof.signature,
              },
            }),
          };
          const response = await submitTurnAsync(turnBody);
          const acceptedTurnId = response?.turn_id ?? handle.turnId;
          // Without `user_message_ids` this settles as a plain accept.
          handle.accept({
            turn_id: acceptedTurnId,
            ...(response?.user_message_ids && {
              user_message_ids: response.user_message_ids,
            }),
          });
          if (completionTurnRef.current?.turnId === handle.turnId) {
            completionTurnRef.current = { turnId: handle.turnId, acceptedTurnId };
          }
          const draftSkillCount = args.restoreContext?.draft?.segments.filter(
            (segment) => segment.kind === 'skill'
          ).length;
          const skill_count = resolveWorkSkillCount({
            computerUseSkill: args.computerUseSkill,
            draftSkillCount,
          });
          const referenced = buildWorkReferencedObjectProps({
            linkedItems: args.restoreContext?.linkedItems,
            optimisticAttachments,
            attachmentCount: attachment_ids?.length ?? 0,
            routineName: args.routineName,
          });
          const priorUserMessages = chatItemsRef.current.filter(
            (item) => item.kind === 'user_message'
          ).length;
          // Zero-based: optimistic user bubble is already in items at accept time.
          const turn_index = Math.max(0, priorUserMessages - 1);
          lastSubmitAnalyticsRef.current = {
            ...lastSubmitAnalyticsRef.current,
            turn_index,
            ...(skill_count != null ? { skill_count } : {}),
            ...referenced,
          };
          trackWorkMessageSubmitted((billingProps) => ({
            action: 'submitted',
            project_id: projectId,
            agent_run_id: agentRunId,
            surface:
              args.analytics?.surface === 'in_session' ? 'in_session' : 'work_home',
            work_mode: args.analytics?.work_mode,
            ...(args.analytics?.entry_point
              ? { entry_point: args.analytics.entry_point }
              : {}),
            ...(args.analytics?.clinical_session_id
              ? { clinical_session_id: args.analytics.clinical_session_id }
              : {}),
            query_length: wirePrompt.length,
            attachment_count: attachment_ids?.length ?? 0,
            has_slash_command:
              args.analytics?.has_slash_command ?? wirePrompt.trimStart().startsWith('/'),
            has_client_capabilities: Boolean(client_capabilities),
            has_computer_use_skill: Boolean(args.computerUseSkill),
            turn_index,
            ...(skill_count != null ? { skill_count } : {}),
            ...referenced,
            ...billingProps,
          }));
        } catch (error) {
          handle.reject(error);
          // Never forward raw error.message — clinical strings can carry PHI.
          trackProjectChatErrorOnce({
            project_id: projectId,
            agent_run_id: agentRunId,
            surface: 'project_chat',
            error_type: 'submit_failed',
          });
        } finally {
          setIsSubmitting(false);
          submitInFlightRef.current = false;
        }
      };
      void runPipeline();
      return handle.operation;
    },
    [
      projectId,
      agentRunId,
      approvalModeRef,
      knownApprovalModeRef,
      runTriggerTypeRef,
      submitTurnAsync,
      beginTurnOperation,
      desktop,
      workspaceRef,
      viewedDocumentRef,
      intl,
      desktopTargetEnabled,
      desktopTargetOverride,
      shareGating.readOnly,
      desktopTargetRequirement,
      chatItemsRef,
      trackWorkApprovalActioned,
      trackWorkMessageSubmitted,
      trackProjectChatErrorOnce,
    ]
  );

  const pendingApprovalToolCallIds = useMemo(() => {
    const ids = new Set<string>();
    for (const approval of chat.run?.approvals ?? []) {
      if (approval.status === 'pending') ids.add(approval.tool_call_id);
    }
    return ids;
  }, [chat.run?.approvals]);

  const cancelTurn = useCallback(() => chat.handleCancelTurn(), [chat]);

  return {
    items: chat.items,
    agentRunId,
    recordingEligible,
    readOnly: shareGating.readOnly,
    ownerDisplayName: shareGating.ownerDisplayName,
    canFork: shareGating.canFork,
    streamStatus: chat.streamStatus,
    computerUseDiscoveries,
    childRuns: chat.run?.child_runs ?? [],
    desktopTargetEnabled,
    dataScope: projectQuery.data?.data_scope,
    viewedDocument: viewedDocumentInSession,
    status: chat.run?.status,
    title: projectQuery.data?.title ?? chat.run?.title,
    isLoading: projectQuery.isLoading || chat.isLoading,
    turnInFlight: chat.turnInFlight,
    isSubmitting,
    cancelling: chat.cancelling,
    startTurn,
    approvalMode,
    setApprovalMode,
    pendingApprovalToolCallIds,
    resolveApproval: chat.onResolveApproval,
    resolvingApproval: chat.resolvingApproval,
    loaderPhrases: chat.loaderPhrases,
    cancelTurn,
    discardedTurnNonce,
    discardedTurn,
  };
};

const useViewedNoteContent = (sessionId: string) =>
  useQuerySessionById({
    sessionId,
    select: (session) => session?.consult_note?.result ?? null,
  }).data;

const useViewedSessionDocument = (sessionId: string, documentId: string) =>
  useQueryDocumentById({
    sessionId,
    documentId,
    select: (doc) =>
      doc ? { content: doc.content ?? null, title: doc.name ?? null } : null,
  }).data;

const useViewedSessionLetter = (sessionId: string, letterId: string, enabled: boolean) =>
  useQuery({
    ...getWorkHostPort('integrations').lettersQueryOptions(sessionId),
    enabled,
    select: (letters) => letters.find((entry) => entry.id === letterId),
  }).data;

let viewedDocumentReads: ViewedDocumentReads | undefined;

const getViewedDocumentReads = (): ViewedDocumentReads => {
  if (viewedDocumentReads) return viewedDocumentReads;
  const { TabType } = getWorkHostPort('session');
  viewedDocumentReads = {
    tabs: {
      note: TabType.NOTE,
      document: TabType.DOCUMENT,
      prechart: TabType.PRECHART,
      letter: TabType.LETTER,
    },
    useNoteContent: useViewedNoteContent,
    useSessionDocument: useViewedSessionDocument,
    useSessionLetter: useViewedSessionLetter,
  } as ViewedDocumentReads;
  return viewedDocumentReads;
};

const useProjectTurnDesktopPort = (): ProjectTurnDesktopPort => {
  const queryClient = useQueryClient();
  // The gate returns a fresh function each render. Call it through a ref so
  // the port object the submit callback closes over keeps one identity.
  const ensurePermissionsRef = useRef<(kind: string) => Promise<boolean>>(() =>
    Promise.resolve(false)
  );
  const gate = getWorkHostPort('desktop').useDesktopPermissionGate();
  ensurePermissionsRef.current = (kind) => gate.ensurePermissions(kind);
  return useMemo<ProjectTurnDesktopPort>(
    () => ({
      isAvailable: () => rpaBridge.isAvailable(),
      ensureComputerUsePermissions: () => ensurePermissionsRef.current('computer_use'),
      resolveDesktopTarget: ({ enabled, override }) =>
        getWorkHostPort('desktop').resolveDesktopTargetTurn(queryClient, {
          desktopTargetEnabled: enabled,
          override,
        }) as Promise<ProjectTurnDesktopTarget>,
      getClientCapabilities: getProjectClientCapabilities,
      getInstanceId: async () => {
        const desktop = getWorkHostPort('desktop');
        const instanceId = await desktop.readNativeInstanceId();
        return instanceId !== null && desktop.isValidDesktopInstanceId(instanceId)
          ? instanceId
          : null;
      },
      signProjectTurn: async ({ projectId, turnId, targetId, unsignedBody }) => {
        const canonicalBody = canonicalize(unsignedBody);
        if (canonicalBody === undefined) {
          throw new Error('Projects turn body could not be canonicalized');
        }
        return rpaBridge.desktopTargetSignProjectTurn({
          projectId,
          turnId,
          targetId,
          canonicalBody,
        });
      },
    }),
    [ensurePermissionsRef, queryClient]
  );
};

const applySlideStreamUpdates = async (updates: SlideStreamUpdate[]): Promise<void> => {
  const session = getWorkHostPort('session');
  for (const update of updates) {
    if (update.type === 'slide_delete') session.handleSlideDeleteChunk(update);
    else await session.handleSlideUpdateChunk(update);
  }
};

const observeForeignStreamEvent: RunStreamEventObserver = (_id, event) => {
  getWorkHostPort('bridge').handleEvidenceChunkStreamEvent(event);
  const slideUpdates = extractSlideStreamUpdates(event);
  if (slideUpdates.length > 0) void applySlideStreamUpdates(slideUpdates);
};

const trackWorkRunCompleted: TrackWorkBilledEvent = (withBilling) => {
  const shell = getWorkHostPort('shell');
  shell.trackWorkEvent(shell.workLaunchEventName.WorkRunCompleted, {
    ...withBilling(shell.resolveWorkBillingAnalyticsProps()),
    client_platform: shell.getPlatform(),
  });
};

const trackWorkMessageSubmitted: TrackWorkBilledEvent = (withBilling) => {
  const shell = getWorkHostPort('shell');
  shell.trackWorkEvent(
    shell.workLaunchEventName.WorkMessageSubmitted,
    withBilling(shell.resolveWorkBillingAnalyticsProps())
  );
};

const trackWorkApprovalActioned: TrackWorkApprovalActioned = (props) => {
  const shell = getWorkHostPort('shell');
  shell.trackWorkEvent(shell.workLaunchEventName.WorkApprovalActioned, props);
};

let projectTransportPorts: ProjectTransportPorts | undefined;

/** One object for every chat. Members read the host ports when called, not at import. */
const getProjectTransportPorts = (): ProjectTransportPorts => {
  if (projectTransportPorts) return projectTransportPorts;
  projectTransportPorts = {
    useSubmitTurn: useBoundSubmitProjectTurn,
    invalidateLists,
    useDesktopPort: useProjectTurnDesktopPort,
    get viewedDocumentReads() {
      return getViewedDocumentReads();
    },
    onForeignStreamEvent: observeForeignStreamEvent,
    turnSettled: {
      invalidateCreditAllowance: () => {
        void invalidateCreditAllowance();
      },
      get invalidateShellCaches() {
        // eslint-disable-next-line @typescript-eslint/unbound-method -- the core calls this function by identity
        return getWorkHostPort('desktop').invalidateShellCachesOnEvent;
      },
    },
    trackWorkRunCompleted,
    trackWorkMessageSubmitted,
    trackWorkApprovalActioned,
    get trackProjectChatErrorOnce() {
      // eslint-disable-next-line @typescript-eslint/unbound-method -- the core calls this function by identity
      return getWorkHostPort('shell').trackProjectChatErrorOnce;
    },
  };
  return projectTransportPorts;
};

export type UseProjectTransportOptions = {
  /**
   * Override the canvas-derived workspace snapshot. Used by Work-in-session
   * where the LHS tabs are the screen and no canvas is mounted.
   */
  workspace?: WorkspaceSnapshotV1;
  /** Limit desktop-target resolution to explicit computer-use commands. */
  desktopTargetRequirement?: ProjectTransportOptions['desktopTargetRequirement'];
};

/** Live binding the bound hook calls, so a suite can spy the core without a self-import. */
export const projectTransportCore = {
  useProjectTransportCore,
};

export const useProjectTransport = (
  projectId: string | undefined,
  options?: UseProjectTransportOptions
): ProjectTransport => {
  const desktopTargetOverride = useDesktopTargetViewSelection()?.override ?? null;
  const canvasWorkspace = useWorkspaceSnapshot();
  return projectTransportCore.useProjectTransportCore(
    projectId,
    {
      workspace: options?.workspace ?? canvasWorkspace,
      desktopTargetRequirement: options?.desktopTargetRequirement,
      desktopTargetOverride,
    },
    getProjectTransportPorts()
  );
};
